import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, Anchor, Briefcase, Wine } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import LeadForm from '../components/LeadForm';
import MsRubiesBadge, { RUBIES_LOGO } from '../components/MsRubiesBadge';
import { apiClient } from '../lib/api';
import { HERO, HOME } from '../constants/testIds';

const HERO_IMG = 'https://images.unsplash.com/photo-1548574505-5e239809ee19?auto=format&fit=crop&w=2400&q=85';

const PARTY_VIBES = [
    {
        name: 'DJ Nights',
        icon: 'fa-solid fa-record-vinyl',
        tag: 'Open-air sky-deck takeovers',
        image: 'https://images.pexels.com/photos/19874598/pexels-photo-19874598.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
        blurb: 'Resident headliners and surprise guest DJs spin from sunset to sunrise on the top deck. Bass drops, laser canopies, confetti cannons over the Caribbean — this is your front-row pass to the loudest night at sea.',
        location: 'Top deck · Royal Caribbean & Virgin Voyages flagships',
    },
    {
        name: 'Karaoke',
        icon: 'fa-solid fa-microphone-lines',
        tag: 'Neon lounges · song battles',
        image: 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?auto=format&fit=crop&w=1600&q=85',
        blurb: 'Private neon-lit karaoke suites with stadium mics, themed costume bins, and a leaderboard that rewards the bravest performance of the night. Friend groups, bachelorette crews, and shy first-timers all leave legends.',
        location: 'Sky lounge · curated by your Cruise Vibes host',
    },
    {
        name: 'Beach Parties',
        icon: 'fa-solid fa-umbrella-beach',
        tag: 'Private island takeovers',
        image: 'https://images.unsplash.com/photo-1530541930197-ff16ac917b0e?auto=format&fit=crop&w=1600&q=85',
        blurb: 'Private beach club takeovers on CocoCay, Half Moon Cay & Labadee — daybeds, bottle service, live percussion, fire dancers at golden hour. Powder sand under your toes, frozen rum in your hand, your favorite people on the next lounger.',
        location: 'CocoCay · Half Moon Cay · Labadee · Bimini',
    },
    {
        name: 'Cocktails',
        icon: 'fa-solid fa-martini-glass-citrus',
        tag: 'Mixology masterclass',
        image: 'https://images.unsplash.com/photo-1551024709-8f23befc6f87?auto=format&fit=crop&w=1600&q=85',
        blurb: 'Hands-on tasting flights with award-winning mixologists — Caribbean rum lab, mezcal smoke shows, and a signature Ms. Ruby cocktail created exclusively for our guests. Sip, swirl, learn the secret recipes to take home.',
        location: 'Speakeasy bar · sunset masterclass slots',
    },
    {
        name: 'Theme Nights',
        icon: 'fa-solid fa-mask',
        tag: 'White · Neon · Decades',
        image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1600&q=85',
        blurb: 'Every sailing has its signature: All-White Yacht Night, Neon Glow Pool Party, 80s Decades Disco, Carnival in Color. Dress codes provided, photo booths everywhere, and the kind of group photos that own the family chat for years.',
        location: 'Pool deck · Grand atrium · Main theater',
    },
    {
        name: 'Singles Mixers',
        icon: 'fa-solid fa-heart',
        tag: 'Curated speed-mingles',
        image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=1600&q=85',
        blurb: 'Hosted sunset deck mixers, themed dinner tables, and a private group chat the moment you book. Singles cruises sell out fastest — our hosts curate intros so you skip the awkward and land at the good part: the after-party.',
        location: 'Sunset deck · private mingles app group',
    },
];

export default function HomePage() {
    const [reviews, setReviews] = useState([]);
    const [openVibe, setOpenVibe] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        apiClient.get('/reviews').then((r) => setReviews(r.data)).catch(() => {});
    }, []);

    return (
        <div>
            {/* HERO */}
            <section data-testid={HERO.section} className="relative min-h-[92vh] flex items-end overflow-hidden">
                <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-bg/15 to-brand-bg/85" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_15%,rgba(127,203,255,0.2),transparent_55%),radial-gradient(ellipse_at_80%_90%,rgba(255,163,116,0.3),transparent_55%)]" />
                <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10 pb-16 pt-32 w-full">
                    <div className="text-xs uppercase tracking-[0.3em] text-white bg-brand-bg/40 backdrop-blur inline-flex px-3 py-1.5 rounded-full mb-6 items-center gap-3">
                        <Sparkles size={14} className="text-brand-gold" /> Luxury · Party · Concierge
                    </div>
                    <h1 className="font-display font-black tracking-tighter uppercase text-5xl sm:text-7xl lg:text-[8rem] leading-[0.95] max-w-5xl text-white drop-shadow-[0_4px_24px_rgba(13,61,107,0.75)]">
                        Book the cruise <br />
                        <span className="font-serif-italic font-medium text-gradient-sunset normal-case">everyone talks about</span>
                    </h1>
                    <p className="text-base sm:text-lg text-white max-w-2xl mt-6 leading-relaxed drop-shadow-[0_2px_12px_rgba(13,61,107,0.7)]">
                        Luxury vacations, unforgettable parties, incredible destinations, and exclusive experiences — all planned for you.
                    </p>
                </div>
            </section>

            {/* RUBIES GLOBAL SERVICES — Co-brand feature */}
            <section data-testid="home-rubies-feature" className="relative max-w-7xl mx-auto px-5 lg:px-10 -mt-10 relative z-20">
                <div className="relative overflow-hidden rounded-3xl border border-white/20 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
                    <img
                        src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1800&q=85"
                        alt="Palm trees, white sand and turquoise ocean"
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/10 to-transparent" />
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_50%,rgba(128,0,32,0.18),transparent_60%)]" />
                    <div className="relative grid lg:grid-cols-5 gap-6 lg:gap-10 p-6 sm:p-10 lg:p-14 items-center">
                        <div className="lg:col-span-2 flex flex-col items-center gap-5">
                            <img
                                src={RUBIES_LOGO}
                                alt="Rubies Global Services"
                                data-testid="home-rubies-logo"
                                className="w-full max-w-sm lg:max-w-md h-auto object-contain drop-shadow-[0_18px_36px_rgba(247,2,42,0.45)]"
                            />
                            <div className="relative">
                                <div className="absolute inset-0 rounded-full bg-[#f7022a]/35 blur-2xl scale-105"></div>
                                <img
                                    src="https://customer-assets.emergentagent.com/job_voyage-party/artifacts/ahbvqg05_ChatGPT%20Image%20Jun%206%2C%202026%2C%2009_42_31%20PM.png"
                                    alt="Ms. Ruby — your concierge"
                                    data-testid="home-msruby-portrait"
                                    className="relative h-44 w-44 sm:h-52 sm:w-52 lg:h-60 lg:w-60 rounded-full object-cover ring-4 ring-[#f7022a]/80 shadow-[0_24px_60px_-16px_rgba(247,2,42,0.7)]"
                                />
                                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#f7022a] text-white text-[10px] uppercase tracking-[0.25em] font-bold shadow-lg whitespace-nowrap">
                                    Meet Ms. Ruby
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-span-3 text-white">
                            <div className="text-[10px] uppercase tracking-[0.35em] text-white font-bold mb-3 flex items-center gap-2 drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
                                <span className="h-px w-8 bg-white/70"></span>
                                Cruise Vibes × Rubies Global Services
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95] text-white drop-shadow-[0_4px_18px_rgba(0,0,0,0.65)]">
                                Book your cruise <br /> with <span style={{ color: '#f7022a', textShadow: '0 0 30px rgba(247,2,42,0.65), 0 2px 10px rgba(0,0,0,0.6)' }}>Ms. Ruby</span>
                            </h2>
                            <p className="text-white mt-5 text-base sm:text-lg leading-relaxed max-w-xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
                                Every Cruise Vibes trip is hand-curated by Ms. Ruby and the Rubies Global Services concierge team — global access, local insight, white-glove planning from boarding to disembark.
                            </p>
                            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm text-white mt-7 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                                <li className="flex items-center gap-2"><i className="fa-solid fa-globe" style={{ color: '#f7022a' }}></i> Global cruise network</li>
                                <li className="flex items-center gap-2"><i className="fa-solid fa-crown" style={{ color: '#f7022a' }}></i> VIP access & perks</li>
                                <li className="flex items-center gap-2"><i className="fa-solid fa-headset" style={{ color: '#f7022a' }}></i> 24/7 dedicated concierge</li>
                                <li className="flex items-center gap-2"><i className="fa-solid fa-gem" style={{ color: '#f7022a' }}></i> Price-match guarantee</li>
                            </ul>
                            <div className="flex flex-wrap gap-3 mt-8">
                                <Button
                                    data-testid="home-rubies-book"
                                    onClick={() => document.querySelector('[data-testid="home-lead-form"]')?.scrollIntoView({ behavior: 'smooth' })}
                                    className="rounded-full h-12 px-7 uppercase tracking-widest font-bold text-white shadow-[0_14px_40px_-10px_rgba(247,2,42,0.9)] hover:brightness-110"
                                    style={{ backgroundColor: '#f7022a' }}
                                >
                                    Book With Ms. Ruby <ArrowRight size={16} className="ml-2" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHY US */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28" data-testid={HOME.whyCards}>
                <div className="grid lg:grid-cols-3 gap-6">
                    {[
                        { i: Briefcase, t: 'We Handle Everything', d: 'Cruise booking, flights, hotels, excursions, transportation — every detail, end to end.', accent: 'text-brand-aqua' },
                        { i: Wine, t: 'VIP Experiences', d: 'Private parties, exclusive excursions, nightlife access, drink packages, cabana reservations.', accent: 'text-brand-gold' },
                        { i: Anchor, t: 'Stress-Free Planning', d: 'You arrive, we deliver. Personal concierge during your trip. Real humans, real fast.', accent: 'text-brand-pink' },
                    ].map(({ i: Icon, t, d, accent }) => (
                        <div key={t} className="bg-brand-surface border border-white/5 rounded-2xl p-8 hover:-translate-y-2 hover:border-brand-aqua/50 transition-all duration-300">
                            <Icon className={accent} size={28} />
                            <h3 className="font-display text-2xl font-bold uppercase tracking-tight mt-5 mb-3">{t}</h3>
                            <p className="text-white/65 text-sm leading-relaxed">{d}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* PARTY SECTION */}
            <section data-testid={HOME.party} className="relative my-20 overflow-hidden">
                <div className="max-w-7xl mx-auto px-5 lg:px-10">
                    <div className="grid lg:grid-cols-2 gap-10 items-center bg-brand-surface2 rounded-3xl overflow-hidden border border-white/5">
                        <div className="p-10 lg:p-14">
                            <div className="text-xs uppercase tracking-[0.25em] text-brand-pink mb-4">Party cruises</div>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter">
                                We bring the <br /><span className="text-gradient-sunset">party</span> to the ocean
                            </h2>
                            <p className="text-white/70 mt-5 max-w-md">DJ events. Karaoke nights. Theme parties. Singles meetups. Group games. Cocktail experiences. Beach parties. We curate the energy — you live the moment.</p>
                            <div className="flex flex-wrap gap-2 mt-8">
                                {PARTY_VIBES.map((v) => (
                                    <button
                                        key={v.name}
                                        type="button"
                                        onClick={() => setOpenVibe(v)}
                                        data-testid={`party-vibe-${v.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                        className="group inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-brand-pink/40 text-brand-pink px-3 py-1.5 rounded-full hover:bg-brand-pink hover:text-brand-bg hover:border-brand-pink hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                                    >
                                        <i className={`${v.icon} text-[11px] opacity-80 group-hover:opacity-100`}></i>
                                        {v.name}
                                    </button>
                                ))}
                            </div>
                            <Button onClick={() => navigate('/party')} className="btn-sunset mt-9 rounded-full h-12 px-7 uppercase tracking-widest font-semibold">
                                Join the Next Adventure
                            </Button>
                        </div>
                        <div className="relative h-72 lg:h-full min-h-[420px]">
                            <img src="https://images.pexels.com/photos/19874598/pexels-photo-19874598.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Party deck" className="absolute inset-0 h-full w-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-r from-brand-surface2 via-transparent to-transparent lg:block hidden" />
                        </div>
                    </div>
                </div>

                <Dialog open={!!openVibe} onOpenChange={(v) => !v && setOpenVibe(null)}>
                    <DialogContent
                        data-testid="party-vibe-dialog"
                        className="bg-brand-surface border-white/10 max-w-2xl p-0 overflow-hidden"
                    >
                        {openVibe && (
                            <div>
                                <div className="relative aspect-[16/9] overflow-hidden">
                                    <img src={openVibe.image} alt={openVibe.name} className="absolute inset-0 h-full w-full object-cover" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-brand-surface via-brand-surface/40 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-6">
                                        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-brand-pink mb-2">
                                            <i className={`${openVibe.icon}`}></i>
                                            Party cruises
                                        </div>
                                        <h3 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white">
                                            {openVibe.name}
                                        </h3>
                                        <div className="text-white/75 mt-1 font-serif-italic text-sm">{openVibe.tag}</div>
                                    </div>
                                </div>
                                <div className="p-6 sm:p-8">
                                    <p className="text-white/85 leading-relaxed text-base">{openVibe.blurb}</p>
                                    <div className="mt-5 text-[11px] uppercase tracking-[0.25em] text-brand-aqua flex items-center gap-2">
                                        <i className="fa-solid fa-location-dot"></i>
                                        {openVibe.location}
                                    </div>
                                    <div className="flex flex-wrap gap-3 mt-7">
                                        <Button
                                            data-testid="party-vibe-book-btn"
                                            onClick={() => {
                                                setOpenVibe(null);
                                                setTimeout(() => document.querySelector('[data-testid="home-lead-form"]')?.scrollIntoView({ behavior: 'smooth' }), 100);
                                            }}
                                            className="btn-sunset rounded-full px-6 h-11 uppercase tracking-widest font-semibold"
                                        >
                                            Request Quote
                                        </Button>
                                        <Button
                                            data-testid="party-vibe-explore-btn"
                                            onClick={() => { setOpenVibe(null); navigate('/party'); }}
                                            variant="ghost"
                                            className="rounded-full px-6 h-11 uppercase tracking-widest text-white/70 hover:text-white"
                                        >
                                            Explore Party Cruises
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </section>

            {/* REVIEWS */}
            <section data-testid={HOME.reviews} className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
                <div className="text-center mb-12">
                    <div className="text-xs uppercase tracking-[0.25em] text-brand-aqua mb-3">From the deck</div>
                    <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight">Real <span className="font-serif-italic font-medium text-brand-gold">trips</span>. Real reviews.</h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {reviews.slice(0, 4).map((r, i) => (
                        <div key={i} className="bg-brand-surface border border-white/5 rounded-2xl p-6 hover:border-brand-gold/30 transition-colors">
                            <div className="text-brand-gold mb-3">{'★'.repeat(r.rating)}</div>
                            <p className="text-sm text-white/80 leading-relaxed mb-5">"{r.text}"</p>
                            <div className="text-xs uppercase tracking-widest text-white/50">{r.name}</div>
                            <div className="text-[10px] uppercase tracking-widest text-brand-aqua">{r.trip}</div>
                        </div>
                    ))}
                </div>
                <div className="text-center mt-10">
                    <button
                        data-testid="home-see-all-reviews"
                        onClick={() => navigate('/reviews')}
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand-aqua hover:text-brand-gold transition-colors border-b border-brand-aqua/30 hover:border-brand-gold pb-1"
                    >
                        See all reviews · share your story <i className="fa-solid fa-arrow-right text-[10px]"></i>
                    </button>
                </div>
            </section>

            {/* LEAD FORM */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-24">
                <div className="grid lg:grid-cols-5 gap-10 items-center">
                    <div className="lg:col-span-2">
                        <div className="text-xs uppercase tracking-[0.25em] text-brand-pink mb-3">Free consultation</div>
                        <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight">
                            Tell us your <span className="font-serif-italic font-medium text-brand-gold">vibe.</span>
                        </h2>
                        <p className="text-white/70 mt-5">No bots, no scripts. A real travel concierge designs your trip — itinerary, cabin, excursions, transfers, parties. You approve. We book.</p>
                        <div className="mt-8 space-y-3 text-sm text-white/70">
                            <div className="flex items-center gap-3"><i className="fa-solid fa-bolt text-brand-aqua"></i> 24-hour quote turnaround</div>
                        </div>
                    </div>
                    <div className="lg:col-span-3"><LeadForm /></div>
                </div>
            </section>
        </div>
    );
}
